import { ReactNode } from 'react';
import { sideMenuAnimation } from '@/components/SideMenu';
import { useDrawerContext } from '@/contexts/DrawerContext';
import { LoadingButton } from '@mui/lab';
import { Box, SxProps } from '@mui/material';
import { Formik, Form, FormikConfig, FormikValues, FormikProps } from 'formik';

type IFormProps<T = ObjectLiteral> = FormikConfig<T> & {
  buttonText?: string;
  buttonLayoutFixed?: boolean;
  children: ReactNode | ((props: FormikProps<T>) => ReactNode);
  sx?: SxProps;
};

export const CustomForm = <Values extends FormikValues = FormikValues>({
  children,
  onSubmit,
  buttonText = 'Enviar',
  buttonLayoutFixed = false,
  sx,
  ...rest
}: IFormProps<Values>) => {
  const { isDrawerOpen } = useDrawerContext();
  return (
    <Formik
      onSubmit={async (values, formikHelpers) => {
        await onSubmit(values, formikHelpers);

        formikHelpers.setSubmitting(false);
      }}
      validateOnChange={false}
      validateOnBlur={false}
      {...rest}
    >
      {formikProps => (
        <Box
          component={Form}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: ' space-between',
            flex: 1,
            height: '100%',
            ...(buttonLayoutFixed && { marginBottom: 10 }),
            ...sx,
          }}
        >
          {typeof children === 'function' ? children(formikProps) : children}
          {buttonLayoutFixed ? (
            <Box
              sx={theme => ({
                position: 'fixed',
                zIndex: 998,
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100%',
                maxWidth: '480px',
                ...sideMenuAnimation,
                [theme.breakpoints.up('md')]: {
                  left: isDrawerOpen
                    ? 'calc(50% + 256px / 2)'
                    : 'calc(50% + 56px / 2)',
                },
              })}
            >
              <Box
                sx={{
                  paddingY: 3,
                  paddingX: 2,
                  backgroundColor: 'background.paper',
                  boxShadow: '0px 0 6px rgb(0 0 0 / 25%)',
                  borderRadius: '6px 6px 0 0',
                }}
              >
                <LoadingButton
                  loading={formikProps.isSubmitting}
                  variant="contained"
                  type="submit"
                  fullWidth
                >
                  {buttonText}
                </LoadingButton>
              </Box>
            </Box>
          ) : (
            <LoadingButton
              loading={formikProps.isSubmitting}
              variant="contained"
              type="submit"
              fullWidth
            >
              {buttonText}
            </LoadingButton>
          )}
        </Box>
      )}
    </Formik>
  );
};
